import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from 'antd';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { useSessionFormState } from '@hooks/useSessionFormState';
import assert from 'assert';
import { useSessionAction } from '@hooks/useSessionAction';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import useAppointmentMeta from '@components/Clinicians/Session/hooks/useAppointmentMeta';
import useAppointmentCaseMeta from '@components/Clinicians/Session/hooks/useAppointmentCaseMeta';
import { SessionFormItem } from '../../Components/SessionFormItem';
import { ApptActionCodes } from 'vl-common/src/schemas/getMetaAppointments';

export const DischargeReasonFormItem = ({ actionCode }: { actionCode: ApptActionCodes }) => {
  const { t } = useTranslation('common');
  const { appointment } = useSessionAppointment();
  const { SUBTYPE_KEY, ACTION_KEY, DISCHARGE_REASON_KEY } = useSessionAction(actionCode);
  const appointmentId = useSessionAppointmentId();

  const { detailsCurrentAppointment } = useAppointmentDetails(appointmentId);

  const { data: appointmentsMeta } = useAppointmentMeta();
  const { data: caseMeta } = useAppointmentCaseMeta();

  assert(appointmentsMeta?.appt_version !== 1, 'DischargeReasonFormItem can only be used with V2 appointments');

  const savedDischargeAction = useMemo(
    () => detailsCurrentAppointment?.appt_actions?.find(({ appt_action_code }) => appt_action_code === actionCode),
    [detailsCurrentAppointment?.appt_actions]
  );

  const [dischargeSubtype] = useSessionFormState(SUBTYPE_KEY, savedDischargeAction?.action_subtype_code || undefined);
  const [dischargeReason, setDischargeReason] = useSessionFormState(
    DISCHARGE_REASON_KEY,
    savedDischargeAction?.discharge_code || undefined
  );

  const [, setDischargeAction] = useSessionFormState(ACTION_KEY);

  useEffect(() => {
    if (!dischargeSubtype) return;

    if (dischargeSubtype === 'DISCHARGE') {
      setDischargeAction({
        appt_action_code: actionCode,
        action_subtype_code: 'DISCHARGE',
        discharge_code: dischargeReason
      });

      return;
    }

    setDischargeReason(undefined);
    setDischargeAction({
      appt_action_code: actionCode,
      action_subtype_code: dischargeSubtype
    });
  }, [dischargeSubtype, dischargeReason, setDischargeAction, setDischargeReason]);

  if (!caseMeta?.case_discharge_reasons) return null;

  return (
    <SessionFormItem
      name={DISCHARGE_REASON_KEY}
      label={t('What’s the reason for discharge?')}
      required={dischargeSubtype === 'DISCHARGE'}
    >
      <Select
        value={dischargeReason}
        onChange={setDischargeReason}
        disabled={dischargeSubtype !== 'DISCHARGE' || appointment.appointment_completed}
        options={caseMeta?.case_discharge_reasons.map(({ discharge_desc, discharge_code }) => ({
          value: discharge_code,
          label: discharge_desc
        }))}
        data-testid="discharge-reason"
      />
    </SessionFormItem>
  );
};
